// TaxEngineInstallations.ts
import {
  toTaxCloudAddressFromObject,
  upsertTaxCloudCartMulti,
  Refund,
  type TaxCloudAddress,
  type UiFee, // ✅ use the module's UiFee type (id is required)
} from "components/Tax/InstallationandRemovalTaxcloud";

import { postInstallationManualTax, postSpecialFeeManualTax, getActiveTaxType } from "components/Tax/InstallationandremovalsmanualTax";


export type RoleType = "signage" | "realtor" | "agent";
export type Mode = "create" | "edit" | "view";

export type Destination = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
} | null;

export type InstallInput = {
  installation_cost: number;
  taxable?: boolean;
  tax_percentage?: number | null; // manual-only
  dynamic_cost_id?: string | null;
  job_types?: string[] | null;
};

export type SpecialFeeInput = {
  id: string | number;            // we will coerce to string
  name: string;
  amount: number;
  taxable?: boolean;
  tax_percentage?: number | null; // manual-only
  tax_rule_id?: string;
  tax_group_id?: string;
};

export type Product = {
  sku: string;
  price: number;
  quantity: number;
  itemType?: "default" | "shipping" | "special_fee" | "service";
  /** When true, manual tax is applied to this product line when TaxCloud is not integrated */
  taxable?: boolean;
}

export type EngineInput = {
  token: string;
  supplierId: string;  // for getActiveTaxType + userId in taxcloud upsert
  ownerId: string;     // for manual endpoints
  roleType: RoleType;
  mode: Mode;
  destination: Destination;

  installation: InstallInput | null;
  specialFees: SpecialFeeInput[];
  products?: Product[];
  cartId?: string;
  orderId?: string;
  taxstatus?: string;
};

export type TaxLine = {
  itemId: string;
  itemType: "InstallationCost" | "SpecialFee" | "default" | "shipping" | "RemovalCost";
  price: number;
  quantity: number;
  tax?: number;
  rate?: number;
  name?: string;
};

export type EngineTotals = {
  installation_subtotal: number;
  installation_tax_amount: number;
  specialfee_subtotal: number;
  specialfee_tax_amount: number;
  specialfee_ids: string[] | number[] | null;
  specialfee_names: string[];
  grand_total: number;
  taxLines?: TaxLine[];
  cartId?: string;
  /** Subtotal of taxable products (manual path) */
  product_subtotal?: number;
  /** Tax amount applied to taxable products (manual path) */
  product_tax_amount?: number;
  // ✅ new
  specialfee?: Array<{
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    tax_percentage?: number | null;
    tax_amount?: number | null;
    tax_rule_id?: string;
    tax_group_id?: string;
  }>;
};

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// ----------------- MANUAL PATH -----------------
async function runManual(input: EngineInput): Promise<EngineTotals> {
  const { token, ownerId, installation, specialFees, destination, supplierId, products } = input;

  // ✅ Guard: Check if required data is available before running manual tax
  if (!destination || !supplierId || !ownerId) {
    // console.log("[TAX:MANUAL] skipped - missing required data:", {
    //   hasDestination: !!destination,
    //   hasSupplierId: !!supplierId,
    //   hasOwnerId: !!ownerId,
    // });
    return {
      installation_subtotal: 0,
      installation_tax_amount: 0,
      specialfee_subtotal: 0,
      specialfee_tax_amount: 0,
      specialfee_ids: [],
      specialfee_names: [],
      grand_total: 0,
      specialfee: [],
    };
  }

  let iSub = 0, iTax = 0, fSub = 0, fTax = 0;
  let installTaxRate: number | undefined;
  let apiFeeRows: any[] = [];   // ← will hold fees.rows from API

  // Installation (qty fixed to 1)
  if (installation) {
    const inst = await postInstallationManualTax(token, ownerId, {
      installation_cost: Number(installation.installation_cost || 0),
      destination: destination ?? null,
      dynamic_cost_id: installation.dynamic_cost_id ?? null,
      job_types: installation.job_types ?? [],
      supplierId,
    });
    iSub = Number(inst?.installation_subtotal || 0);
    iTax = Number(inst?.installation_tax_amount || 0);
    installTaxRate = typeof inst?.tax_rate === "number" ? inst.tax_rate : undefined;
  }

  // Special fees (many → totals) — use API response rows
  if (Array.isArray(specialFees) && specialFees.length) {
    const fees = await postSpecialFeeManualTax(token, ownerId, {
      destination: destination ?? null,
      fees: specialFees.map((f) => ({
        id: String(f.id),
        name: String(f.name ?? "Special Fee"),
        amount: Number(f.amount || 0),
        taxable: f.taxable !== false,
        // these are required as strings in this call-site's type
        tax_rule_id: String(f.tax_rule_id ?? ""),
        tax_group_id: String(f.tax_group_id ?? ""),
        // optional
        tax_percentage: f.tax_percentage != null ? Number(f.tax_percentage) : null,
      })),
    });

    // ← your log shows this shape
    // {
    //   specialfee_subtotal: 133,
    //   specialfee_tax_amount: 13.3,
    //   rows: [{ specialfee_id, specialfee_name, specialfee_amount, specialfee_tax_percentage, specialfee_tax_amount, specialfee_taxable }, ...]
    // }
    fSub = Number(fees?.specialfee_subtotal || 0);
    fTax = Number(fees?.specialfee_tax_amount || 0);
    apiFeeRows = Array.isArray(fees?.rows) ? fees.rows : [];
  }

  // ✅ Build the specialfee array to return:
  // Prefer API rows; if unavailable, fall back to the input specialFees
  // ✅ Create a map to preserve original fee data (tax_rule_id, tax_group_id)
  const originalFeeById = new Map(
    (specialFees || []).map(f => [String(f.id), f])
  );
  
  const specialfeeRows =
    apiFeeRows.length > 0
      ? apiFeeRows.map((r) => {
        const originalFee = originalFeeById.get(String(r.specialfee_id || ""));
        return {
          id: String(r.specialfee_id || ""),
          name: String(r.specialfee_name || "Special Fee"),
          amount: Number(r.specialfee_amount || 0),
          taxable: r.specialfee_taxable !== false,
          tax_percentage:
            typeof r.specialfee_tax_percentage === "number"
              ? Number(r.specialfee_tax_percentage)
              : null,
          tax_amount:
            typeof r.specialfee_tax_amount === "number"
              ? Number(r.specialfee_tax_amount)
              : null,
          // ✅ Preserve these from original input (needed for order submission)
          tax_rule_id: originalFee?.tax_rule_id ? String(originalFee.tax_rule_id) : undefined,
          tax_group_id: originalFee?.tax_group_id ? String(originalFee.tax_group_id) : undefined,
        };
      })
      : (specialFees || []).map((f) => ({
        id: String(f.id),
        name: String(f.name ?? "Special Fee"),
        amount: Number(f.amount || 0),
        taxable: f.taxable !== false,
        tax_percentage: f.tax_percentage != null ? Number(f.tax_percentage) : null,
        tax_amount: (f as any).tax_amount != null ? Number((f as any).tax_amount) : null,
        tax_rule_id: f.tax_rule_id ? String(f.tax_rule_id) : undefined,
        tax_group_id: f.tax_group_id ? String(f.tax_group_id) : undefined,
      }));

  // Derive ids/names from whatever we’re returning
  // Taxable products: apply manual tax using installation tax rate when is_taxable
  let pSub = 0, pTax = 0;
  const safeProducts = Array.isArray(products) ? products : [];
  const rate = typeof installTaxRate === "number" ? installTaxRate / 100 : 0;
  for (const p of safeProducts) {
    if (p.taxable !== true) continue;
    const price = Number(p.price ?? 0);
    const qty = Number(p.quantity ?? 1);
    const lineTotal = price * qty;
    pSub += lineTotal;
    pTax += lineTotal * rate;
  }
  pSub = r2(pSub);
  pTax = r2(pTax);

  const retIds = specialfeeRows.map((f) => String(f.id));
  const retNames = specialfeeRows.map((f) => String(f.name || "Special Fee"));

  return {
    installation_subtotal: r2(iSub),
    installation_tax_amount: r2(iTax),
    specialfee_subtotal: r2(fSub),
    specialfee_tax_amount: r2(fTax),
    specialfee_ids: retIds,
    specialfee_names: retNames,
    product_subtotal: pSub,
    product_tax_amount: pTax,
    grand_total: r2(iSub + iTax + fSub + fTax + pSub + pTax),

    // 👇 send the full rows built from the API (or fallback)
    specialfee: specialfeeRows,
  };
}



// Build TaxCloud product lines (qty fixed to 1)
// function buildProducts(install: InstallInput | null): { sku: string; price: number; quantity: number }[] {
//   if (!install) return [];
//   const price = Number(install.installation_cost || 0);
//   if (price <= 0) return [];
//   return [{ sku: "InstallationCost", price, quantity: 1 }];
// }

// // Map fees → module UiFee[] with REQUIRED id (string)
// function mapFeesToUiFees(fees: SpecialFeeInput[]): UiFee[] {
//   return (fees || [])
//     .map((f, idx) => {
//       // ✅ Fallback: if no id, use name; if no name, generate a stable key
//       const rawId = (f.id ?? f.name ?? `fee-${idx}`).toString().trim();
//       const id = rawId.length ? rawId : `fee-${idx}`;

//       return {
//         id,                                     // REQUIRED by UiFee
//         title: f.name ?? `Special Fee ${idx + 1}`,
//         amount: Math.max(0, Number(f.amount || 0)),
//         taxable: f.taxable !== false,
//       } as UiFee;
//     })
//     // Optionally drop zero-amount lines (keep if your API needs them)
//     .filter((x) => x.amount > 0);
// }

// ----------------- TAXCLOUD PATH -----------------
async function runTaxCloud(input: EngineInput): Promise<EngineTotals> {
  const { token, supplierId, destination, installation, specialFees, cartId, ownerId, products } = input;
  // console.log("runTaxCloud", input);
  
  // ✅ Guard: Check if required data is available before running TaxCloud
  if (!destination || !supplierId || !ownerId) {
    // console.log("[TAX:CLOUD] skipped - missing required data:", {
    //   hasDestination: !!destination,
    //   hasSupplierId: !!supplierId,
    //   hasOwnerId: !!ownerId,
    // });
    return {
      installation_subtotal: 0,
      installation_tax_amount: 0,
      specialfee_subtotal: 0,
      specialfee_tax_amount: 0,
      specialfee_ids: [],
      specialfee_names: [],
      grand_total: 0,
      specialfee: [],
    };
  }
  
  // 1) Build non-null TaxCloudAddress
  const destMaybe = toTaxCloudAddressFromObject({
    addressLine1: destination?.addressLine1,
    city: destination?.city,
    state: destination?.state || destination?.stateCode,
    zip: destination?.zip,
  });
  if (!destMaybe) {
    // console.log("[TAX:CLOUD] Invalid destination address");
    return {
      installation_subtotal: 0,
      installation_tax_amount: 0,
      specialfee_subtotal: 0,
      specialfee_tax_amount: 0,
      specialfee_ids: [],
      specialfee_names: [],
      grand_total: 0,
      specialfee: [],
    };
  }
  const dest: TaxCloudAddress = destMaybe;

  // // 2) Upsert cart using your exact signature
  // const products = buildProducts(installation);          // [{ sku: "InstallationCost", price, quantity: 1 }] or []
  // const taxableFees = mapFeesToUiFees(specialFees);      // UiFee[] with required id

  // ✅ Preserve itemType from input products (service for installation, special_fee for fees).
  // Callers must only include line_items products that are non-rotational and have valid source_product_id (exclude placeholder id).
  const safeProducts: Product[] = Array.isArray(products) ? products : [];
  // if (safeProducts.length === 0 && installation && Number(installation.installation_cost || 0) > 0) {
  //   safeProducts.push({ sku: "InstallationCost", price: Number(installation.installation_cost || 0), quantity: 1 });
  // }

  const upsertResp = await upsertTaxCloudCartMulti({
    token,
    // your function expects userId = customerId; supplierId can be passed too if your impl reads it
    userId: ownerId,
    supplierId,                  // keep if your impl uses it; safe to include
    cartId,
    destination: dest,
    products: safeProducts,      // ✅ Now preserves itemType (service/special_fee)
    shipping: null,
    taxableFees: [],
    // originOverride: null,     // let helper choose default origin
  });

  // 3) Parse the NEW TaxCloud response shape you posted (items[0].lineItems[*])
  //    Example you showed:
  //    upsertResp = { items: [ { cartId, lineItems: [{ itemId, price, quantity, tax: {amount, rate}}] } ], ... }
  const firstItem = (upsertResp as any)?.items?.[0] ?? {};
  const rawLineItems: any[] = Array.isArray(firstItem?.lineItems) ? firstItem.lineItems : [];

  // ...same code above that builds dest, calls upsertTaxCloudCartMulti, and gets rawLineItems...

  const feeNames = new Set((specialFees || []).map((f) => String(f.name || "").trim()).filter(Boolean));

  // Normalize into our taxLines structure; product lines (sku) are "default", not SpecialFee
  const taxLines: TaxLine[] = rawLineItems.map((ln: any) => {
    const itemId = String(ln?.itemId ?? "");
    const price = Number(ln?.price ?? 0);
    const quantity = Number(ln?.quantity ?? 1);
    const tax = Number(ln?.tax?.amount ?? 0);
    const rate = Number(ln?.tax?.rate ?? 0);

    const itemType: TaxLine["itemType"] =
      itemId === "InstallationCost" ? "InstallationCost" :
      itemId === "RemovalCost" ? "RemovalCost" :
      feeNames.has(itemId) ? "SpecialFee" : "default";

    return { itemId, itemType, price, quantity, tax, rate, name: itemId };
  });

  // ✅ Totals from server response first (source of truth); include product lines for product_subtotal/product_tax_amount
  let installSub = 0, feeSub = 0, installTax = 0, feeTax = 0;
  let productSub = 0, productTax = 0;

  for (const ln of taxLines) {
    if (ln.itemType === "InstallationCost") {
      installSub += Number(ln.price || 0);
      installTax += Number(ln.tax || 0);
    } else if (ln.itemType === "RemovalCost") {
      installSub += Number(ln.price || 0);
      installTax += Number(ln.tax || 0);
    } else if (ln.itemType === "default") {
      productSub += Number(ln.price || 0) * Number(ln.quantity ?? 1);
      productTax += Number(ln.tax || 0);
    } else {
      feeSub += Number(ln.price || 0);
      feeTax += Number(ln.tax || 0);
    }
  }

  // Fallbacks (in case server didn’t echo all expected lines)
  if (installSub === 0 && installation) {
    installSub = Number(installation.installation_cost || 0);
  }
  if (feeSub === 0 && Array.isArray(specialFees)) {
    feeSub = specialFees.reduce((s, f) => s + Number(f.amount || 0), 0);
  }

  // ✅ Build a map: fee name → tax line (to enrich fees with calculated tax)
  const taxLineByName = new Map<string, TaxLine>();
  for (const ln of taxLines) {
    if (ln.itemType === "SpecialFee") {
      taxLineByName.set(ln.name || ln.itemId, ln);
    }
  }

  const out: EngineTotals = {
    installation_subtotal: r2(installSub),
    installation_tax_amount: r2(installTax),
    specialfee_subtotal: r2(feeSub),
    specialfee_tax_amount: r2(feeTax),
    specialfee_names: specialFees?.map((f) => f.name) ?? [],
    specialfee_ids: specialFees?.map((f) => String(f.id)),
    product_subtotal: r2(productSub),
    product_tax_amount: r2(productTax),
    grand_total: r2(installSub + installTax + feeSub + feeTax + productSub + productTax),
    taxLines,
    specialfee: (specialFees || []).map(f => {
      // ✅ Find matching tax line from TaxCloud response
      const taxLine = taxLineByName.get(f.name);
      
      return {
        id: String(f.id),
        name: String(f.name ?? "Special Fee"),
        amount: Number(f.amount || 0),
        taxable: f.taxable !== false,
        tax_percentage: taxLine?.rate ?? (f.tax_percentage != null ? Number(f.tax_percentage) : null), // ✅ Use TaxCloud rate if available
        tax_amount: taxLine?.tax ?? null, // ✅ Use calculated tax from TaxCloud
        tax_rule_id: f.tax_rule_id ? String(f.tax_rule_id) : undefined,
        tax_group_id: f.tax_group_id ? String(f.tax_group_id) : undefined,
      };
    }),
  };

  // cartId passthrough (keep your existing logic)
  const respCartId =
    (upsertResp as any)?.cartId ??
    (typeof upsertResp === "string" ? upsertResp : undefined) ??
    firstItem?.cartId;
  if (respCartId) (out as any).cartId = String(respCartId);

  return out;
}

export async function requestInstallationTaxRefund(token: string, orderNumber: string): Promise<boolean> {
  return Refund(token, orderNumber);
}

// ----------------- PUBLIC ENTRY -----------------
// export async function runTaxEngineForInstallations(input: EngineInput): Promise<EngineTotals> {
//   const { token, supplierId, roleType, mode, destination, installation, specialFees, ownerId, cartId } = input;
//   console.log("runTaxEngineForInstallations", input);
//   console.log("roletype", { roleType, mode });
//   const type = await getActiveTaxType(token, supplierId); // "manual" | "taxcloud"
//   console.log("runTaxEngineForInstallations", { type });

//   if (type === "manual") {
//     return await runManual(input);
//   }

//   // type === "taxcloud" — follow your routing; all branches run the same callback
//   if (input.roleType === "signage" && mode === "view") return await runTaxCloud(input);
//   if (input.roleType === "realtor" || input.roleType === "agent") return await runTaxCloud(input);
//   return await runTaxCloud(input);
// }

export async function runTaxEngineForInstallations(input: EngineInput): Promise<EngineTotals> {
  const { token, supplierId, roleType, mode, destination, installation, specialFees, ownerId, cartId, products } = input;
  // console.log("runTaxEngineForInstallations", input);
  
  // ✅ Guard: Check if required data is available BEFORE running any tax calculation
  if (!destination || !supplierId || !ownerId) {
    // console.log("[TAX:ENGINE] skipped - missing required data:", {
    //   hasDestination: !!destination,
    //   hasSupplierId: !!supplierId,
    //   hasOwnerId: !!ownerId,
    // });
    return {
      installation_subtotal: 0,
      installation_tax_amount: 0,
      specialfee_subtotal: 0,
      specialfee_tax_amount: 0,
      specialfee_ids: [],
      specialfee_names: [],
      grand_total: 0,
      specialfee: [],
    };
  }
  
  // 2) Fallback for any other roles: use active tax type

  const type1 = await getActiveTaxType(token, supplierId); // "manual" | "taxcloud"
  // console.log("[TaxEngine] active type:", type1);

  if (type1 === "taxcloud") {
    if (roleType === "signage" && (mode === "view")) {
      const r = await runTaxCloud(input);
      return r;
    } else if (roleType === "realtor" || roleType === "agent") {
      // console.log("isdidi", input);
      const r = await runTaxCloud(input);
      return r;
    } else if (roleType === "signage"  && (mode === "create" || mode === "edit")) {
      // ✅ Return empty data for signage create/edit - don't run any tax calculation
      return {
        installation_subtotal: 0,
        installation_tax_amount: 0,
        specialfee_subtotal: 0,
        specialfee_tax_amount: 0,
        specialfee_ids: [],
        specialfee_names: [],
        grand_total: 0,
        specialfee: [],
      };
    }
    else {
      // console.log("isdidiunderscore", input);
      const r = await runTaxCloud(input);
      return r;
    }
  }
  else {
    return await runManual(input);
  }
}


/*
Parent: call createTaxCloudOrder/completeTaxCloudOrder ONLY when paymentStatus === 103.

if (paymentStatus === 103 && totals.cartId && orderId) {
  await createTaxCloudOrder(token, totals.cartId, orderId);
  await completeTaxCloudOrder(token, orderId, new Date().toISOString());
}
*/
